"use client";
import { useBooleanState } from "@gettactic/helpers/src";
import { parseFloorIdFromUrl } from "@gettactic/helpers/src/resources/selectFloor";
import { useAuthenticated } from "@gettactic/hooks";
import {
  parseQueryObject,
  useUrlQuerySync
} from "@/features/dashboard/hooks/useUrlQuerySync";
import { useRouter } from "@/lib/useRouter";
import React from "react";

export const useMapSelectedOfficeUrlSync = (
  enabled = true,
  initialFloorIdParam?: string
) => {
  const isLoaded = useBooleanState();

  const {
    userContext: { authenticatedUser, currentOffice },
    setUserContext
  } = useAuthenticated();

  const router = useRouter();

  const [initialFloorId, setInitialFloorId] = React.useState<string | null>(
    initialFloorIdParam ?? null
  );

  useUrlQuerySync(
    {
      officeId: currentOffice.id
    },
    (paramsFromUrl) => {
      // Support old URL format with hash
      const [, hash] = (router.asPath as string).split("#");
      const parsedHash = new URLSearchParams(hash);
      const officeId = parsedHash.get("officeId") || null;

      const urlOfficeId = String(officeId || paramsFromUrl.officeId);

      if (urlOfficeId && urlOfficeId !== currentOffice.id) {
        const office = authenticatedUser.offices?.byId[urlOfficeId];
        if (office) {
          setUserContext({
            type: "updateCurrentOffice",
            payload: office.id
          });
        } else {
          console.warn(`Office with ID ${urlOfficeId} not found.`);
        }
      }
    },
    enabled && router.isReady,
    false,
    true
  );

  React.useEffect(() => {
    // Wait for router to be ready before parsing URL params
    if (!router.isReady) {
      return;
    }

    // Support old URL format with hash
    const parsed = parseQueryObject(router.query);
    const [, hash] = (router.asPath as string).split("#");
    const parsedHash = new URLSearchParams(hash);
    const officeId = parsedHash.get("officeId") || null;

    // Use utility function for safe floor ID parsing
    const floorIdFromQuery = parsed.floorId;
    const initialFloorId = parseFloorIdFromUrl(
      Array.isArray(floorIdFromQuery)
        ? floorIdFromQuery.map(String)
        : floorIdFromQuery
          ? String(floorIdFromQuery)
          : undefined,
      hash
    );

    setInitialFloorId(initialFloorId);

    if (officeId && officeId !== currentOffice.id) {
      setUserContext({
        type: "updateCurrentOffice",
        payload: officeId || ""
      });
      return;
    }

    isLoaded.true();
  }, [router.isReady, currentOffice.id]);

  return {
    initialFloorId,
    isLoaded: isLoaded.state
  };
};
